import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { TerminosDialog } from "../components/TerminosDialog";
import { TERMINOS_VERSION } from "../data/terminos";
import { ArrowLeft, UserPlus, FileText, Upload, CheckCircle, AlertCircle, Info, ShieldCheck, Plus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { CatastralInput } from "../components/CatastralInput";
import { SectorCombobox } from "../components/SectorCombobox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// Mes previo al actual (con rollover de año). Ej. Julio 2026 → { mes: "Junio", anio: 2026 }.
// Enero 2027 → { mes: "Diciembre", anio: 2026 }.
const mesPrevio = () => {
  const d = new Date();
  d.setDate(1); // evita saltos por fin de mes (ej. 31 marzo → 3 marzo)
  d.setMonth(d.getMonth() - 1);
  return { mes: MESES[d.getMonth()], anio: d.getFullYear() };
};
const YEAR_NOW = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => YEAR_NOW - i);

const REQUIRED = "text-red-500";
const inputCls = "h-10 rounded-lg border-green-200 focus-visible:ring-green-600";

// Convierte campos null/undefined en "" o false para no romper inputs controlados
// (evita "Cannot read properties of null" al hacer .trim() sobre valores precargados).
function sanitizeContrib(c) {
  const out = {};
  const stringFields = [
    "tipo_documento", "documento", "nombre", "razon_social",
    "denominacion_comercial", "actividad_economica",
    "ultimo_pago_mes",
    "estado_nombre", "municipio_nombre", "parroquia_nombre", "sector",
    "direccion_fiscal", "punto_referencia", "numero_local",
    "email", "celular", "telefono",
    "cedula_representante_legal", "nombre_representante_legal", "celular_representante_legal",
    "codigo_catastral", "cuenta_contrato_electricidad", "observaciones",
    "rif_imagen_url", "rif_imagen_nombre",
    "documento_catastro_url", "documento_catastro_nombre",
    "estado_registro", "rechazado_motivo",
  ];
  for (const k of stringFields) {
    if (k in c) out[k] = c[k] == null ? "" : String(c[k]);
  }
  if ("area_total_establecimiento" in c) out.area_total_establecimiento = c.area_total_establecimiento == null ? "" : c.area_total_establecimiento;
  if ("ultimo_pago_anio" in c) out.ultimo_pago_anio = c.ultimo_pago_anio == null ? "" : c.ultimo_pago_anio;
  if ("sin_codigo_catastral" in c) out.sin_codigo_catastral = !!c.sin_codigo_catastral;
  return out;
}

// Medidor de fortaleza de contraseña — 0..5.
// Criterios: ≥8, ≥12, mayúscula+minúscula, número, símbolo.
function scorePassword(pwd) {
  const p = String(pwd || "");
  if (!p) return { score: 0, label: "", cls: "bg-slate-200", text: "text-slate-500", pct: 0 };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  const meta = [
    { label: "Muy débil", cls: "bg-red-500",     text: "text-red-700"    },
    { label: "Débil",     cls: "bg-red-400",     text: "text-red-600"    },
    { label: "Aceptable", cls: "bg-amber-400",   text: "text-amber-700"  },
    { label: "Buena",     cls: "bg-lime-500",    text: "text-lime-700"   },
    { label: "Fuerte",    cls: "bg-green-500",   text: "text-green-700"  },
    { label: "Excelente", cls: "bg-emerald-600", text: "text-emerald-800" },
  ][s];
  return { score: s, label: meta.label, cls: meta.cls, text: meta.text, pct: (s / 5) * 100 };
}


export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Comercio, 2: Ubicación + Contacto, 3: Documentos + Cuenta
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Mostrar/ocultar contraseña y confirmación (icono de ojo)
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  // Catálogos públicos
  const [municipios, setMunicipios] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [actividades, setActividades] = useState([]);

  // Diálogo para crear nuevo sector desde el propio registro
  const [newSectorOpen, setNewSectorOpen] = useState(false);
  const [newSectorName, setNewSectorName] = useState("");
  const [savingSector, setSavingSector] = useState(false);
  const saveNewSector = async () => {
    const nombre = String(newSectorName || "").trim();
    if (nombre.length < 3) { toast.error("El nombre debe tener al menos 3 caracteres"); return; }
    try {
      setSavingSector(true);
      const r = await axios.post(`${API}/auth/public/sectores`, { nombre });
      const nuevo = r.data.sector;
      setSectores(prev => [...prev, nuevo].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setForm(f => ({ ...f, sector: nuevo.nombre }));
      setNewSectorOpen(false); setNewSectorName("");
      toast.success(`Sector "${nombre}" creado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el sector");
    } finally { setSavingSector(false); }
  };

  useEffect(() => {
    axios.get(`${API}/auth/public/municipios`).then(r => setMunicipios(r.data || [])).catch(() => { });
    axios.get(`${API}/auth/public/sectores`).then(r => setSectores(r.data || [])).catch(() => { });
    axios.get(`${API}/auth/public/actividades`).then(r => setActividades(r.data || [])).catch(() => { });
  }, []);

  const [form, setForm] = useState({
    // 1) Comercio
    tipo_documento: "J", documento: "",
    nombre: "", denominacion_comercial: "",
    actividad_economica: "",
    area_total_establecimiento: "",
    ultimo_pago_mes: mesPrevio().mes, ultimo_pago_anio: mesPrevio().anio,
    // 2) Ubicación + contacto
    estado_nombre: "Aragua", municipio_nombre: "", parroquia_nombre: "",
    sector: "", direccion_fiscal: "", punto_referencia: "", numero_local: "",
    email: "", celular: "", telefono: "",
    // 3) Rep. legal / códigos / documentos / password
    cedula_representante_legal: "", nombre_representante_legal: "", celular_representante_legal: "",
    codigo_catastral: "", sin_codigo_catastral: false,
    cuenta_contrato_electricidad: "",
    observaciones: "",
    rif_imagen_url: "", rif_imagen_nombre: "",
    documento_catastro_url: "", documento_catastro_nombre: "",
    password: "", password2: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // --- Modo actualización: si el RIF ya existe en pre_aprobado o rechazado,
  //     verificamos identidad y pre-llenamos el formulario para editar.
  const [updateMode, setUpdateMode] = useState(false);   // true si estamos actualizando
  const [verifyToken, setVerifyToken] = useState("");    // token corto emitido por /verify-identity
  const [prevEstado, setPrevEstado] = useState("");      // "pre_aprobado" | "rechazado"
  const [verifyDialog, setVerifyDialog] = useState({ open: false, method: "celular", value: "", busy: false, error: "" });

  // Validación en vivo de RIF y Email (debounced 500 ms). Estados:
  //   null | "checking" | "ok" | "existe" | "actualizable"  (el último = puede editar via verify-identity)
  const [rifCheck, setRifCheck] = useState({ status: null, estado_registro: null });
  const [emailCheck, setEmailCheck] = useState({ status: null, estado_registro: null });

  useEffect(() => {
    if (updateMode) { setRifCheck({ status: null, estado_registro: null }); return; } // RIF fijo en modo update
    const doc = (form.documento || "").trim();
    if (doc.length < 4) { setRifCheck({ status: null, estado_registro: null }); return; }
    setRifCheck(s => ({ ...s, status: "checking" }));
    const t = setTimeout(() => {
      axios.get(`${API}/auth/public/check-rif`, { params: { documento: doc } })
        .then(r => {
          const state = r.data?.state || (r.data?.exists ? "existe" : "ok");
          setRifCheck({ status: state, estado_registro: r.data?.estado_registro || null });
        })
        .catch(() => setRifCheck({ status: null, estado_registro: null }));
    }, 500);
    return () => clearTimeout(t);
  }, [form.documento, updateMode]);

  useEffect(() => {
    if (updateMode) { setEmailCheck({ status: null, estado_registro: null }); return; } // en update, el email puede coincidir con el propio registro
    const em = (form.email || "").trim();
    if (!/.+@.+\..+/.test(em)) { setEmailCheck({ status: null, estado_registro: null }); return; }
    setEmailCheck(s => ({ ...s, status: "checking" }));
    const t = setTimeout(() => {
      axios.get(`${API}/auth/public/check-email`, { params: { email: em } })
        .then(r => setEmailCheck({
          status: r.data?.exists ? "existe" : "ok",
          estado_registro: r.data?.estado_registro || null,
        }))
        .catch(() => setEmailCheck({ status: null, estado_registro: null }));
    }, 500);
    return () => clearTimeout(t);
  }, [form.email, updateMode]);

  // El botón "Continuar" del paso 1 se bloquea si:
  //  - el RIF existe como ACTIVO ("existe") — no puede seguir; debe iniciar sesión.
  //  - si es "actualizable", NO bloqueamos; mostraremos banner con botón para verificar identidad.
  const rifBusy = rifCheck.status === "existe";
  const emailBusy = emailCheck.status === "existe";

  const [isPrecargado, setIsPrecargado] = useState(false);
  const [terminosAceptados, setTerminosAceptados] = useState(false);
  const [terminosOpen, setTerminosOpen] = useState(false);
  // ¿Es un flujo que CREA usuario? En esos casos, los términos son obligatorios.
  // - Registro nuevo → siempre obligatorio.
  // - Precargado (isPrecargado=true) → obligatorio porque crea usuario nuevo.
  // - Actualización normal (updateMode && !isPrecargado) → opcional (ya aceptó antes).
  const terminosRequeridos = !updateMode || isPrecargado;

  const openVerify = () => {
    // Si es precargado (state="completable"), saltamos el doble factor: no hay contacto que verificar.
    if (rifCheck.status === "completable") {
      startCompletable();
      return;
    }
    setVerifyDialog({ open: true, method: "celular", value: "", busy: false, error: "" });
  };
  const startCompletable = async () => {
    try {
      const body = { documento: form.documento.trim().toUpperCase() };
      const r = await axios.post(`${API}/auth/public/verify-identity`, body);
      const c = sanitizeContrib(r.data?.contribuyente || {});
      setForm(f => ({ ...f, ...c, password: "", password2: "" }));
      setVerifyToken(r.data?.verify_token || "");
      setPrevEstado("precargado");
      setIsPrecargado(true);
      setUpdateMode(true);
      toast.success("Encontramos su comercio. Complete los datos faltantes para activar su portal.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos cargar sus datos");
    }
  };
  const doVerify = async () => {
    const { method, value } = verifyDialog;
    if (!value.trim()) { setVerifyDialog(v => ({ ...v, error: "Ingresa el valor para verificar" })); return; }
    setVerifyDialog(v => ({ ...v, busy: true, error: "" }));
    try {
      const body = { documento: form.documento.trim().toUpperCase() };
      if (method === "celular") body.celular = value.trim();
      else body.email = value.trim().toLowerCase();
      const r = await axios.post(`${API}/auth/public/verify-identity`, body);
      // Éxito: prellenar formulario con los campos devueltos
      const c = sanitizeContrib(r.data?.contribuyente || {});
      setForm(f => ({
        ...f,
        ...c,
        // password vacío — el usuario decide si cambiarla o dejarla vacía para conservar
        password: "", password2: "",
      }));
      setVerifyToken(r.data?.verify_token || "");
      setPrevEstado(c.estado_registro || "");
      setIsPrecargado(!!r.data?.is_precargado);
      setUpdateMode(true);
      setVerifyDialog({ open: false, method: "celular", value: "", busy: false, error: "" });
      toast.success("Identidad verificada. Puedes actualizar tus datos.");
    } catch (e) {
      setVerifyDialog(v => ({ ...v, busy: false, error: e.response?.data?.detail || "No pudimos verificar tu identidad" }));
    }
  };
  const cancelUpdateMode = () => {
    setUpdateMode(false);
    setVerifyToken("");
    setPrevEstado("");
    setIsPrecargado(false);
    // limpiar todos los campos, dejando solo el RIF que el usuario tipeó
    const documento = form.documento;
    setForm({
      tipo_documento: "J", documento,
      nombre: "", denominacion_comercial: "", actividad_economica: "",
      area_total_establecimiento: "", ultimo_pago_mes: mesPrevio().mes, ultimo_pago_anio: mesPrevio().anio,
      estado_nombre: "Aragua", municipio_nombre: "", parroquia_nombre: "",
      sector: "", direccion_fiscal: "", punto_referencia: "", numero_local: "",
      email: "", celular: "", telefono: "",
      cedula_representante_legal: "", nombre_representante_legal: "", celular_representante_legal: "",
      codigo_catastral: "", sin_codigo_catastral: false,
      cuenta_contrato_electricidad: "", observaciones: "",
      rif_imagen_url: "", rif_imagen_nombre: "",
      documento_catastro_url: "", documento_catastro_nombre: "",
      password: "", password2: "",
    });
  };

  // Derivados
  const estados = useMemo(() =>
    Array.from(new Set(municipios.map(m => m.estado_nombre).filter(Boolean))).sort(),
    [municipios]);
  const municipiosDelEstado = useMemo(() =>
    form.estado_nombre ? municipios.filter(m => m.estado_nombre === form.estado_nombre) : [],
    [form.estado_nombre, municipios]);
  const parroquiasDelMunicipio = useMemo(() => {
    const m = municipios.find(mu => mu.nombre === form.municipio_nombre && mu.estado_nombre === form.estado_nombre);
    return (m && Array.isArray(m.parroquias)) ? m.parroquias : [];
  }, [form.municipio_nombre, form.estado_nombre, municipios]);

  // Validación por paso (defensivo: los datos del backend pueden traer null en campos sin llenar)
  const s_ = (v) => (v == null ? "" : String(v)).trim();
  const stepInvalid = (s) => {
    if (s === 1) {
      return !s_(form.tipo_documento) || !s_(form.documento) || !s_(form.nombre)
        || !s_(form.denominacion_comercial) || !s_(form.actividad_economica)
        || !s_(form.area_total_establecimiento) || !s_(form.ultimo_pago_mes) || !s_(form.ultimo_pago_anio)
        || rifBusy;
    }
    if (s === 2) {
      return !s_(form.estado_nombre) || !s_(form.municipio_nombre) || !s_(form.parroquia_nombre)
        || !s_(form.sector) || !s_(form.direccion_fiscal)
        || !s_(form.email) || !s_(form.celular)
        || emailBusy;
    }
    if (s === 3) {
      const codigoOK = form.sin_codigo_catastral || s_(form.codigo_catastral);
      const pw = s_(form.password);
      const pw2 = s_(form.password2);
      // Precargado: password OBLIGATORIA (crea usuario nuevo). Update normal: opcional.
      const passOK = (updateMode && !isPrecargado)
        ? ((!pw && !pw2) || (pw.length >= 6 && pw === pw2))
        : (pw.length >= 6 && pw === pw2);
      const terminosOK = !terminosRequeridos || terminosAceptados;
      return !codigoOK || !passOK || !terminosOK;
    }
    return false;
  };
  const canNext = !stepInvalid(step);

  // Upload archivo (usa endpoint público)
  const uploadFile = async (file, kind) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("El archivo no puede superar 8 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/auth/upload-publico`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "rif") { set("rif_imagen_url", r.data.url); set("rif_imagen_nombre", r.data.nombre); }
      if (kind === "catastro") { set("documento_catastro_url", r.data.url); set("documento_catastro_nombre", r.data.nombre); }
      toast.success(`Archivo cargado: ${r.data.nombre}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir el archivo");
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (form.password || form.password2) {
      if (form.password !== form.password2) return toast.error("Las contraseñas no coinciden");
      if (form.password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    } else if (!updateMode) {
      return toast.error("Debe definir una contraseña");
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      delete payload.password2;
      payload.area_total_establecimiento = Number(form.area_total_establecimiento) || 0;
      payload.ultimo_pago_anio = Number(form.ultimo_pago_anio) || 0;
      if (terminosRequeridos) {
        payload.terminos_aceptados = terminosAceptados;
        payload.terminos_version = TERMINOS_VERSION;
      }
      if (updateMode) {
        // Actualización o completar
        payload.verify_token = verifyToken;
        if (!payload.password) delete payload.password; // conservar la anterior (solo aplica a update normal)
        const r = await axios.post(`${API}/auth/public/update-registration`, payload);
        setSuccess({ ...r.data, mode: isPrecargado ? "complete" : "update", prev_estado: prevEstado });
        toast.success(isPrecargado
          ? "¡Datos completados! Un funcionario activará su cuenta."
          : "Datos actualizados. Un funcionario revisará tu solicitud.");
      } else {
        const r = await axios.post(`${API}/auth/register/con-lae`, payload);
        setSuccess({ ...r.data, mode: "create" });
        toast.success("¡Registro enviado! Un funcionario revisará tu información.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    } finally { setSubmitting(false); }
  };

  // Pantalla de éxito post-registro
  if (success) {
    const isUpdate = success.mode === "update";
    const isComplete = success.mode === "complete";
    const accent = isComplete ? "blue" : (isUpdate ? "amber" : "green");
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="register-success">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 w-full max-w-lg text-center">
          <div className={`w-16 h-16 rounded-2xl bg-${accent}-100 flex items-center justify-center shadow-md mx-auto mb-4`}>
            <CheckCircle className={`w-9 h-9 text-${accent}-700`} />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-green-900">
            {isComplete ? "¡Datos completados!" : (isUpdate ? "¡Datos actualizados!" : "¡Registro recibido!")}
          </h1>
          <p className="text-slate-600 mt-3">
            {isComplete
              ? "Sus datos fueron completados y están pendientes de revisión por un funcionario municipal. Cuando aprueben su solicitud, podrá iniciar sesión en el portal con el correo y contraseña que definió."
              : (isUpdate
                ? "Tus datos fueron actualizados y están pendientes de revisión por un funcionario municipal. Recibirás notificación cuando tu solicitud sea aprobada."
                : "Tu solicitud fue enviada. Un funcionario municipal revisará tu información y activará tu cuenta.")}
          </p>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mt-5 text-left">
            <p className="text-xs font-bold text-amber-900 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> ¿Qué sigue?</p>
            <ul className="list-disc list-inside text-xs text-amber-800 mt-1 space-y-0.5">
              <li>Tu solicitud queda en estado <b>Pre-Aprobado</b>.</li>
              <li>Recibirás notificación cuando sea aprobada.</li>
              <li>Una vez aprobada, podrás iniciar sesión con tu correo y contraseña.</li>
            </ul>
          </div>
          <Button
            onClick={() => navigate("/")}
            className="mt-6 h-11 rounded-xl bg-green-700 hover:bg-green-800 text-white w-full"
            data-testid="register-success-back-btn"
          >Volver al inicio</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4 py-8" data-testid="register-page">
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium" data-testid="register-back-link">
        <ArrowLeft className="w-4 h-4" /> Volver al inicio
      </Link>

      <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 w-full max-w-3xl my-6">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-green-700 flex items-center justify-center shadow-lg mb-3">
            <UserPlus className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-green-900 text-center">Crear cuenta de contribuyente</h1>
          <p className="text-sm text-slate-500 mt-1 text-center max-w-md">
            Regístrate como <b>Comerciante Con LAE</b>. Un funcionario revisará tu solicitud y activará tu acceso al portal.
          </p>
        </div>

        {/* Banner modo actualización / completar */}
        {updateMode && (
          <div className={`rounded-xl border-2 p-4 mb-5 ${isPrecargado ? "border-blue-300 bg-blue-50" : "border-amber-300 bg-amber-50"}`} data-testid="update-mode-banner">
            <div className="flex items-start gap-3">
              <Info className={`w-5 h-5 mt-0.5 shrink-0 ${isPrecargado ? "text-blue-700" : "text-amber-700"}`} />
              <div className="flex-1">
                <p className={`text-sm font-bold ${isPrecargado ? "text-blue-900" : "text-amber-900"}`}>
                  {isPrecargado
                    ? "Completando datos de un registro precargado"
                    : <>Estás actualizando tu solicitud <span className="uppercase">{prevEstado}</span></>}
                </p>
                <p className={`text-xs mt-1 ${isPrecargado ? "text-blue-800" : "text-amber-800"}`}>
                  {isPrecargado
                    ? "Su RIF ya estaba en el registro municipal. Al completar los campos faltantes y enviar, un funcionario revisará su información y activará su cuenta de portal."
                    : <>Al enviar, tu solicitud volverá a quedar en <b>pre-aprobado</b> y un funcionario revisará los cambios. Puedes dejar la contraseña vacía si deseas conservar la actual.</>}
                </p>
                <button
                  type="button"
                  onClick={cancelUpdateMode}
                  className={`text-[11px] hover:underline mt-1 ${isPrecargado ? "text-blue-800 hover:text-blue-900" : "text-amber-800 hover:text-amber-900"}`}
                  data-testid="btn-cancel-update-mode"
                >Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="register-stepper">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${step >= s ? "bg-green-700 text-white" : "bg-slate-200 text-slate-500"}`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-green-700" : "bg-slate-200"}`}></div>}
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 -mt-4 mb-4">
          {step === 1 && "1 · Datos del Comercio"}
          {step === 2 && "2 · Ubicación y Contacto"}
          {step === 3 && "3 · Documentos y Cuenta"}
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="register-form">

          {/* PASO 1 · COMERCIO */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Tipo <span className={REQUIRED}>*</span></Label>
                <Select value={form.tipo_documento} onValueChange={v => set("tipo_documento", v)}>
                  <SelectTrigger className={inputCls} data-testid="reg-tipo-doc"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="J">J (Jurídica)</SelectItem>
                    <SelectItem value="V">V (Venezolano)</SelectItem>
                    <SelectItem value="E">E (Extranjero)</SelectItem>
                    <SelectItem value="G">G (Gubernamental)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">RIF / Documento <span className={REQUIRED}>*</span></Label>
                <Input
                  data-testid="reg-documento"
                  value={form.documento}
                  onChange={e => set("documento", e.target.value.toUpperCase())}
                  placeholder="Ej: 408512306"
                  className={inputCls}
                  disabled={updateMode}
                />
                <CheckHint check={rifCheck} label="RIF" testid="rif-check" onUpdate={openVerify} />
              </div>

              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Razón Social <span className={REQUIRED}>*</span></Label>
                <Input data-testid="reg-nombre" value={form.nombre} onChange={e => set("nombre", e.target.value)} placeholder="Nombre legal del comercio, C.A." className={inputCls} />
              </div>

              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Denominación Comercial <span className={REQUIRED}>*</span></Label>
                <Input data-testid="reg-denominacion" value={form.denominacion_comercial} onChange={e => set("denominacion_comercial", e.target.value)} placeholder="Nombre con el que opera el comercio" className={inputCls} />
              </div>

              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Actividad Económica <span className={REQUIRED}>*</span></Label>
                <Select value={form.actividad_economica} onValueChange={v => set("actividad_economica", v)}>
                  <SelectTrigger className={inputCls} data-testid="reg-actividad"><SelectValue placeholder="Seleccione una actividad" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {actividades.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
                {actividades.length === 0 && <p className="text-xs text-slate-400">Cargando actividades…</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Área del Establecimiento (m²) <span className={REQUIRED}>*</span></Label>
                <Input data-testid="reg-area" type="number" min="0" step="0.01" value={form.area_total_establecimiento} onChange={e => set("area_total_establecimiento", e.target.value)} placeholder="Ej: 80" className={inputCls} />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-green-900 font-semibold text-sm">Último Pago (Mes) <span className={REQUIRED}>*</span></Label>
                  <Select value={form.ultimo_pago_mes} onValueChange={v => set("ultimo_pago_mes", v)}>
                    <SelectTrigger className={inputCls} data-testid="reg-mes"><SelectValue placeholder="Mes" /></SelectTrigger>
                    <SelectContent>
                      {MESES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-green-900 font-semibold text-sm">Año <span className={REQUIRED}>*</span></Label>
                  <Select value={String(form.ultimo_pago_anio)} onValueChange={v => set("ultimo_pago_anio", Number(v))}>
                    <SelectTrigger className={inputCls} data-testid="reg-anio"><SelectValue placeholder="Año" /></SelectTrigger>
                    <SelectContent>
                      {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* PASO 2 · UBICACIÓN Y CONTACTO */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Estado <span className={REQUIRED}>*</span></Label>
                <Select value={form.estado_nombre} onValueChange={v => setForm(f => ({ ...f, estado_nombre: v, municipio_nombre: "", parroquia_nombre: "" }))}>
                  <SelectTrigger className={inputCls} data-testid="reg-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    {estados.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Municipio <span className={REQUIRED}>*</span></Label>
                <Select value={form.municipio_nombre} onValueChange={v => setForm(f => ({ ...f, municipio_nombre: v, parroquia_nombre: "" }))} disabled={!form.estado_nombre}>
                  <SelectTrigger className={inputCls} data-testid="reg-municipio"><SelectValue placeholder="Municipio" /></SelectTrigger>
                  <SelectContent>
                    {municipiosDelEstado.map(m => <SelectItem key={m.id} value={m.nombre}>{m.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Parroquia <span className={REQUIRED}>*</span></Label>
                <Select value={form.parroquia_nombre} onValueChange={v => set("parroquia_nombre", v)} disabled={!form.municipio_nombre}>
                  <SelectTrigger className={inputCls} data-testid="reg-parroquia"><SelectValue placeholder="Parroquia" /></SelectTrigger>
                  <SelectContent>
                    {parroquiasDelMunicipio.map((p, i) => (
                      <SelectItem key={i} value={typeof p === "string" ? p : (p.nombre || "")}>{typeof p === "string" ? p : (p.nombre || "")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Sector <span className={REQUIRED}>*</span></Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SectorCombobox
                      value={form.sector}
                      onChange={(v) => set("sector", v)}
                      sectores={sectores}
                      placeholder="Buscar y seleccionar sector…"
                      emptyText="No hay sectores cargados. Cree uno con el botón +"
                      testId="reg-sector"
                      triggerClassName={inputCls}
                    />
                  </div>
                  <Dialog open={newSectorOpen} onOpenChange={setNewSectorOpen}>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => setNewSectorOpen(true)}
                      title="Crear nuevo sector si el suyo no está en la lista"
                      data-testid="reg-btn-nuevo-sector"
                      className="border-green-200 text-green-800 hover:bg-green-50 shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle className="text-green-900">Crear nuevo sector</DialogTitle>
                        <DialogDescription>Ingrese el nombre del sector donde se ubica su comercio.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2 py-2">
                        <Label>Nombre del sector*</Label>
                        <Input
                          data-testid="reg-input-nuevo-sector"
                          value={newSectorName}
                          onChange={e => setNewSectorName(e.target.value)}
                          placeholder="Ej. José Félix Ribas"
                          autoFocus
                          onKeyDown={e => { if (e.key === "Enter") saveNewSector(); }}
                        />
                        <p className="text-xs text-slate-500">El sector quedará disponible para futuras selecciones.</p>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setNewSectorOpen(false)} disabled={savingSector}>Cancelar</Button>
                        <Button data-testid="reg-btn-guardar-sector" onClick={saveNewSector} disabled={savingSector} className="bg-green-700 hover:bg-green-800 text-white">{savingSector ? "Guardando..." : "Guardar"}</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Domicilio Fiscal <span className={REQUIRED}>*</span></Label>
                <Input data-testid="reg-direccion" value={form.direccion_fiscal} onChange={e => set("direccion_fiscal", e.target.value)} placeholder="Ej: Av. Principal, casa/local Nº..." className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Nº de Local (opcional)</Label>
                <Input data-testid="reg-nro-local" value={form.numero_local} onChange={e => set("numero_local", e.target.value)} placeholder="Ej: 15-B" className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Punto de Referencia (opcional)</Label>
                <Input data-testid="reg-referencia" value={form.punto_referencia} onChange={e => set("punto_referencia", e.target.value)} placeholder="Al lado de..." className={inputCls} />
              </div>

              <div className="md:col-span-2 border-t border-green-100 pt-3 mt-1">
                <p className="text-xs uppercase font-bold text-green-700 tracking-wider mb-1">Contacto</p>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Correo electrónico <span className={REQUIRED}>*</span></Label>
                <Input data-testid="reg-email" type="email" value={form.email} onChange={e => set("email", e.target.value.trim())} placeholder="ejemplo@correo.com" className={inputCls} />
                <CheckHint check={emailCheck} label="Correo" testid="email-check" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Celular <span className={REQUIRED}>*</span></Label>
                <Input data-testid="reg-celular" value={form.celular} onChange={e => set("celular", e.target.value)} placeholder="0414-XXXXXXX" className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Teléfono fijo (opcional)</Label>
                <Input data-testid="reg-telefono" value={form.telefono} onChange={e => set("telefono", e.target.value)} placeholder="0243-XXXXXXX" className={inputCls} />
              </div>
            </div>
          )}

          {/* PASO 3 · DOCUMENTOS Y CUENTA */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <p className="text-xs uppercase font-bold text-green-700 tracking-wider mb-1">Representante Legal (opcional)</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Cédula</Label>
                <Input data-testid="reg-rep-cedula" value={form.cedula_representante_legal} onChange={e => set("cedula_representante_legal", e.target.value)} placeholder="V-12345678" className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Nombre completo</Label>
                <Input data-testid="reg-rep-nombre" value={form.nombre_representante_legal} onChange={e => set("nombre_representante_legal", e.target.value)} className={inputCls} />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Celular del representante</Label>
                <Input data-testid="reg-rep-celular" value={form.celular_representante_legal} onChange={e => set("celular_representante_legal", e.target.value)} className={inputCls} />
              </div>

              <div className="md:col-span-2 border-t border-green-100 pt-3 mt-1">
                <p className="text-xs uppercase font-bold text-green-700 tracking-wider mb-1">Códigos y servicios</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Código Catastral <span className={REQUIRED}>*</span></Label>
                <CatastralInput
                  testId="reg-cod-catastral"
                  value={form.codigo_catastral}
                  onChange={v => set("codigo_catastral", v)}
                  disabled={form.sin_codigo_catastral}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox id="sin-cod" data-testid="reg-sin-cod" checked={form.sin_codigo_catastral} onCheckedChange={v => setForm(f => ({ ...f, sin_codigo_catastral: !!v, codigo_catastral: v ? "" : f.codigo_catastral }))} />
                  <Label htmlFor="sin-cod" className="text-xs text-slate-600 cursor-pointer">No tengo código catastral</Label>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Cuenta Contrato Electricidad (CORPOELEC)</Label>
                <Input data-testid="reg-cuenta-elec" value={form.cuenta_contrato_electricidad} onChange={e => set("cuenta_contrato_electricidad", e.target.value)} placeholder="Nº de cuenta contrato" className={inputCls} />
              </div>

              <div className="md:col-span-2 border-t border-green-100 pt-3 mt-1">
                <p className="text-xs uppercase font-bold text-green-700 tracking-wider mb-1">Documentos anexos (PDF/JPG · máx 8 MB)</p>
              </div>
              <FileUploadField
                label="RIF (imagen o PDF)"
                testid="reg-file-rif"
                fileName={form.rif_imagen_nombre}
                onChange={(f) => uploadFile(f, "rif")}
                onClear={() => { set("rif_imagen_url", ""); set("rif_imagen_nombre", ""); }}
              />
              <FileUploadField
                label="Ficha Catastral / Contrato Arrendamiento"
                testid="reg-file-catastro"
                fileName={form.documento_catastro_nombre}
                onChange={(f) => uploadFile(f, "catastro")}
                onClear={() => { set("documento_catastro_url", ""); set("documento_catastro_nombre", ""); }}
              />

              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">Observaciones (opcional)</Label>
                <Textarea data-testid="reg-obs" value={form.observaciones} onChange={e => set("observaciones", e.target.value)} rows={2} placeholder="Cualquier información adicional que quiera compartir con el funcionario…" className="rounded-lg border-green-200" />
              </div>

              <div className="md:col-span-2 border-t border-green-100 pt-3 mt-1">
                <p className="text-xs uppercase font-bold text-green-700 tracking-wider mb-1">
                  Datos de acceso al Portal
                  {updateMode && !isPrecargado && <span className="ml-2 text-[10px] normal-case text-amber-700 font-normal">(deja las contraseñas vacías para conservar la actual)</span>}
                  {isPrecargado && <span className="ml-2 text-[10px] normal-case text-blue-700 font-normal">(defina su contraseña para acceder al portal)</span>}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">
                  Contraseña {(!updateMode || isPrecargado) && <span className={REQUIRED}>*</span>}
                </Label>
                <div className="relative">
                  <Input
                    data-testid="reg-password"
                    type={showPass ? "text" : "password"}
                    value={form.password}
                    onChange={e => set("password", e.target.value)}
                    placeholder={updateMode && !isPrecargado ? "Sin cambio" : "Mínimo 6 caracteres"}
                    className={`${inputCls} pr-10`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-green-700 hover:text-green-900 transition-colors"
                    aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                    title={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="reg-password-toggle"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Medidor de fortaleza */}
                {form.password && (() => {
                  const st = scorePassword(form.password);
                  return (
                    <div className="mt-1" data-testid="reg-password-strength">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${st.cls}`}
                            style={{ width: `${st.pct}%` }}
                            data-testid="reg-password-strength-bar"
                          />
                        </div>
                        <span className={`text-[10.5px] font-semibold ${st.text}`} data-testid="reg-password-strength-label">
                          {st.label}
                        </span>
                      </div>
                      {st.score < 3 && (
                        <p className="text-[10px] text-slate-500 leading-tight">
                          Tip: combine mayúsculas, minúsculas, números y algún símbolo (mínimo 8 caracteres).
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-1.5">
                <Label className="text-green-900 font-semibold text-sm">
                  Confirmar contraseña {(!updateMode || isPrecargado) && <span className={REQUIRED}>*</span>}
                </Label>
                <div className="relative">
                  <Input
                    data-testid="reg-password2"
                    type={showPass2 ? "text" : "password"}
                    value={form.password2}
                    onChange={e => set("password2", e.target.value)}
                    className={`${inputCls} pr-10`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass2(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-green-700 hover:text-green-900 transition-colors"
                    aria-label={showPass2 ? "Ocultar contraseña" : "Mostrar contraseña"}
                    title={showPass2 ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="reg-password2-toggle"
                    tabIndex={-1}
                  >
                    {showPass2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form.password2 && form.password !== form.password2 && (
                  <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Las contraseñas no coinciden</p>
                )}
              </div>

              {terminosRequeridos && (
                <div className="md:col-span-2 border-t border-green-100 pt-4 mt-2">
                  <div
                    className={`rounded-lg border-2 p-3 transition ${terminosAceptados
                        ? "border-green-500 bg-green-50/50"
                        : "border-amber-300 bg-amber-50/40"
                      }`}
                    data-testid="terminos-box"
                  >
                    <label htmlFor="acepto-terminos" className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        id="acepto-terminos"
                        data-testid="acepto-terminos"
                        checked={terminosAceptados}
                        onCheckedChange={v => setTerminosAceptados(!!v)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 text-sm">
                        <span className="text-green-900 font-semibold">
                          He leído y acepto los{" "}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setTerminosOpen(true); }}
                            className="text-orange-600 hover:text-orange-800 underline underline-offset-2 font-bold"
                            data-testid="open-terminos-link"
                          >Términos y Condiciones</button>
                          {" "}del Portal Contribuyente SISAUR
                        </span>
                        <span className={REQUIRED}> *</span>
                        <p className="text-[11px] text-slate-600 mt-1">
                          Al aceptar, confirma que los datos ingresados son verídicos y que ha leído íntegramente el documento (versión {TERMINOS_VERSION}).
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botones de navegación */}
          <div className="flex items-center justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1 || submitting}
              data-testid="btn-register-prev"
              className="border-green-200 text-green-800 hover:bg-green-50"
            >Atrás</Button>
            {step < 3 ? (
              <Button
                type="button"
                onClick={() => setStep(s => Math.min(3, s + 1))}
                disabled={!canNext}
                data-testid="btn-register-next"
                className="bg-green-700 hover:bg-green-800 text-white"
              >Continuar</Button>
            ) : (
              <Button
                type="submit"
                disabled={submitting || stepInvalid(3)}
                data-testid="btn-register-submit"
                className={isPrecargado
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : (updateMode ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-orange-500 hover:bg-orange-600 text-white")}
              >{submitting
                ? (isPrecargado ? "Enviando…" : (updateMode ? "Actualizando…" : "Enviando…"))
                : (isPrecargado ? "Completar registro" : (updateMode ? "Enviar actualización" : "Enviar solicitud de registro"))}</Button>
            )}
          </div>

          <p className="text-center text-sm text-slate-500 mt-4">
            ¿Ya tiene cuenta?{" "}
            <Link to="/login/contribuyente" className="text-orange-600 hover:text-orange-700 font-semibold" data-testid="register-login-link">
              Inicie sesión
            </Link>
          </p>
        </form>
      </div>

      {/* Diálogo de Términos y Condiciones */}
      <TerminosDialog open={terminosOpen} onOpenChange={setTerminosOpen} />

      {/* Diálogo de verificación de identidad para modo actualización */}
      <Dialog open={verifyDialog.open} onOpenChange={(o) => { if (!o) setVerifyDialog(v => ({ ...v, open: false })); }}>
        <DialogContent className="max-w-md" data-testid="dialog-verify-identity">
          <DialogHeader>
            <DialogTitle className="text-amber-800 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Verifica tu identidad
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-xs">
              Para actualizar la solicitud del RIF <b className="font-mono">{form.tipo_documento}-{form.documento}</b>, ingresa el celular o correo con el que solicitaste el registro original.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <RadioGroup
              value={verifyDialog.method}
              onValueChange={(v) => setVerifyDialog(s => ({ ...s, method: v, value: "", error: "" }))}
              className="flex gap-4"
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="celular" id="ver-cel" data-testid="verify-method-celular" />
                <span className="text-sm">Celular</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="email" id="ver-em" data-testid="verify-method-email" />
                <span className="text-sm">Correo electrónico</span>
              </label>
            </RadioGroup>
            <div>
              <Label className="text-sm">
                {verifyDialog.method === "celular" ? "Celular registrado" : "Correo registrado"}
              </Label>
              <Input
                data-testid="verify-value"
                value={verifyDialog.value}
                onChange={e => setVerifyDialog(s => ({ ...s, value: e.target.value, error: "" }))}
                placeholder={verifyDialog.method === "celular" ? "0414-XXXXXXX" : "ejemplo@correo.com"}
                className={inputCls}
                type={verifyDialog.method === "email" ? "email" : "text"}
                autoFocus
              />
              {verifyDialog.error && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1" data-testid="verify-error">
                  <AlertCircle className="w-3 h-3" /> {verifyDialog.error}
                </p>
              )}
            </div>
            <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2">
              Debe coincidir <b>exactamente</b> con lo que registraste. Si no lo recuerdas, contacta a la alcaldía.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setVerifyDialog(v => ({ ...v, open: false }))}
              disabled={verifyDialog.busy}
              data-testid="btn-cancel-verify"
            >Cancelar</Button>
            <Button
              type="button"
              onClick={doVerify}
              disabled={verifyDialog.busy || !verifyDialog.value.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-confirm-verify"
            >{verifyDialog.busy ? "Verificando…" : "Verificar y continuar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CheckHint({ check, label, testid, onUpdate }) {
  const s = check?.status;
  if (!s) return null;
  if (s === "checking") {
    return <p className="text-[11px] text-slate-500 flex items-center gap-1" data-testid={`${testid}-checking`}>
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse"></span>
      Verificando…
    </p>;
  }
  if (s === "ok") {
    return <p className="text-[11px] text-green-700 flex items-center gap-1" data-testid={`${testid}-ok`}>
      <CheckCircle className="w-3 h-3" />
      {label} disponible
    </p>;
  }
  if (s === "completable") {
    // RIF existe en la base municipal pero SIN usuario asociado (cargado desde Excel).
    // Puede completar sus datos para crear su cuenta.
    return (
      <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3 mt-1" data-testid={`${testid}-completable`}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-blue-900">
              ¡Encontramos su comercio en el registro municipal!
            </p>
            <p className="text-[11px] text-blue-800 mt-0.5">
              Su RIF ya está en nuestra base pero faltan datos para activar el acceso al portal. <b>Complete su información</b> para que un funcionario active su cuenta.
            </p>
            {onUpdate && (
              <Button
                type="button"
                size="sm"
                onClick={onUpdate}
                className="mt-2 h-8 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid={`${testid}-btn-complete`}
              >Completar mis datos</Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (s === "actualizable") {
    // El RIF existe en estado pre_aprobado o rechazado: ofrecemos actualizar
    const isRechazado = check?.estado_registro === "rechazado";
    return (
      <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 mt-1" data-testid={`${testid}-actualizable`}>
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-amber-900">
              Detectamos una solicitud previa {isRechazado ? "rechazada" : "pendiente"} con este RIF.
            </p>
            <p className="text-[11px] text-amber-800 mt-0.5">
              Debes <b>actualizar tus datos</b> para poder continuar. Verifica tu identidad con el celular o correo con el que solicitaste el registro.
            </p>
            {onUpdate && (
              <Button
                type="button"
                size="sm"
                onClick={onUpdate}
                className="mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid={`${testid}-btn-update`}
              >Actualizar mis datos</Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (s === "existe") {
    return <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid={`${testid}-existe`}>
      <AlertCircle className="w-3 h-3" />
      {`Este ${label.toLowerCase()} ya está registrado y activo. `}
      <Link to="/login/contribuyente" className="underline font-semibold hover:text-red-800">inicia sesión</Link>
    </p>;
  }
  return null;
}

function FileUploadField({ label, testid, fileName, onChange, onClear }) {
  const inputId = `${testid}-input`;
  return (
    <div className="space-y-1.5">
      <Label className="text-green-900 font-semibold text-sm">{label}</Label>
      {fileName ? (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
          <FileText className="w-4 h-4 text-green-700 shrink-0" />
          <span className="text-xs text-green-900 truncate flex-1" title={fileName}>{fileName}</span>
          <button type="button" onClick={onClear} className="text-xs text-red-600 hover:underline" data-testid={`${testid}-clear`}>Quitar</button>
        </div>
      ) : (
        <label htmlFor={inputId} className="flex items-center gap-2 p-2.5 rounded-lg border border-dashed border-green-300 hover:border-green-500 hover:bg-green-50/40 cursor-pointer transition">
          <Upload className="w-4 h-4 text-green-700" />
          <span className="text-xs text-slate-600">Haz clic para adjuntar (PDF/JPG/PNG)</span>
          <input
            id={inputId}
            data-testid={testid}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={e => onChange(e.target.files?.[0])}
          />
        </label>
      )}
    </div>
  );
}
